import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSidebar } from '../context/SidebarContext';
import { usePermissions } from '../context/PermissionContext';
import { 
  LayoutDashboard, Users, CheckSquare, GitFork, FileBarChart, 
  Phone, PieChart, Award, Building2, GitMerge, Inbox, Megaphone, 
  Send, Bot, MessageSquare, History, Users2, Settings, ChevronDown,
  Plus, Calendar, FileText, List, Tag, Zap, LogOut, BarChart3,
  GitCompareArrows, Globe2, Radio
} from 'lucide-react';

/**
 * Sidebar Component with 3-Level Visibility Control
 * 
 * Level 1: defaultVisible (base config)
 * Level 2: Role-based (admin/user)
 * Level 3: Admin settings toggle (from appSettings)
 * 
 * SECTION 7: Responsive sidebar - collapses to 70px, expands to 240px on hover
 */

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Icon mapping
const iconMap = {
  LayoutDashboard, Users, CheckSquare, GitFork, FileBarChart,
  Phone, PieChart, Award, Building2, GitMerge, Inbox, Megaphone,
  Send, Bot, MessageSquare, History, Users2, Settings, Plus,
  Calendar, FileText, List, Tag, Zap, BarChart3, GitCompareArrows
};

// Navigation Item Component
const NavItem = ({ to, icon: Icon, label, collapsed, badge, children, isSubmenu }) => {
  const location = useLocation();
  const isActive = location.pathname === to || location.pathname.startsWith(to + '/');
  const [expanded, setExpanded] = useState(false);

  if (children) {
    return (
      <div>
        <button
          onClick={() => setExpanded(!expanded)}
          className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
            isActive 
              ? 'bg-teal-50 text-teal-700' 
              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
          }`}
        >
          <Icon size={20} className={isActive ? 'text-teal-600' : 'text-gray-400'} />
          {!collapsed && (
            <>
              <span className="flex-1 text-sm font-medium text-left">{label}</span>
              <ChevronDown size={16} className={`transition-transform ${expanded ? 'rotate-180' : ''}`} />
            </>
          )}
        </button>
        {!collapsed && expanded && (
          <div className="ml-6 mt-1 space-y-1">
            {children}
          </div>
        )}
      </div>
    );
  }

  return (
    <NavLink
      to={to}
      className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
        isActive 
          ? 'bg-teal-50 text-teal-700' 
          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
      } ${isSubmenu ? 'py-2' : ''}`}
    >
      <Icon size={isSubmenu ? 16 : 20} className={isActive ? 'text-teal-600' : 'text-gray-400'} />
      {!collapsed && (
        <>
          <span className={`flex-1 ${isSubmenu ? 'text-xs' : 'text-sm'} font-medium`}>{label}</span>
          {badge && (
            <span className="px-2 py-0.5 bg-teal-100 text-teal-700 text-xs rounded-full font-semibold">
              {badge}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
};

// Section Header Component
const SectionHeader = ({ title, collapsed }) => {
  if (collapsed) return null;
  return (
    <div className="px-4 pt-6 pb-2">
      <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{title}</h3>
    </div>
  );
};

const Sidebar = () => {
  const { user, logout, token } = useAuth();
  const { isExpanded, expand, collapse } = useSidebar();
  const { canView, isAdmin: permIsAdmin, loading: permLoading } = usePermissions();
  const [sidebarModules, setSidebarModules] = useState([]);
  
  const isAdmin = user?.role?.toLowerCase() === 'admin';
  const userRole = user?.role?.toLowerCase();

  // Load sidebar modules with order and visibility
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/settings/sidebar-modules`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          const modules = [...(data.modules || [])].sort((a, b) => a.order - b.order);
          setSidebarModules(modules);
        }
      } catch (err) {
        console.error('Failed to load sidebar settings');
      }
    };
    
    if (token) loadSettings();
  }, [token, userRole]);

  /**
   * Single source of truth: user.permissions (via PermissionContext.canView)
   * Priority:
   * 1. Admin always sees everything
   * 2. Permission check: canView(moduleId)
   * 3. Sidebar-modules visibility toggle (admin global setting)
   */
  const isModuleVisible = (moduleId, adminOnly = false) => {
    // iter250 — QC whitelist migrated to permission-based check. Any user
    // (including QC) sees a module iff canView(moduleId), which honours
    // per-user permission overrides so admin-granted extras (e.g. tasks
    // for QC) automatically appear without a code change. Sidebar-modules
    // visibility toggle still runs after the permission gate below.

    // Still loading permissions — hide non-admin modules to prevent flash
    if (!isAdmin && permLoading) return false;
    
    // Admin-only modules hidden from non-admins
    if (adminOnly && !isAdmin) return false;
    
    // RBAC permission check — user must have view access
    if (!isAdmin && !canView(moduleId)) return false;
    
    // Sidebar-modules global visibility toggle
    const module = sidebarModules.find(m => m.id === moduleId);
    if (module && module.visible === false) {
      if (adminOnly && isAdmin) return true;
      return false;
    }
    
    return true;
  };

  // Sidebar collapsed width: 70px, expanded width: 240px
  const sidebarWidth = isExpanded ? 'w-60' : 'w-[70px]';

  return (
    <div 
      className={`sidebar-container fixed left-0 top-0 h-screen bg-white border-r border-gray-200 flex flex-col transition-all duration-300 ease-in-out z-40 ${sidebarWidth}`}
      style={{ overflowY: 'auto' }}
      data-testid="sidebar"
      data-collapsed={!isExpanded}
      onMouseEnter={expand}
      onMouseLeave={collapse}
    >
      {/* Logo */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl flex items-center justify-center shadow-lg">
            <span className="font-bold text-white text-lg">S</span>
          </div>
          {isExpanded && (
            <div>
              <h1 className="font-bold text-gray-800 text-lg">Superhost CRM</h1>
              <p className="text-xs text-gray-400">Enterprise</p>
            </div>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-3 space-y-1">
        {/* CRM Section */}
        <SectionHeader title="CRM" collapsed={!isExpanded} />
        {isModuleVisible('insights') && (
          <NavItem to="/insights" icon={Zap} label="Insights" collapsed={!isExpanded} />
        )}
        {isModuleVisible('leads') && (
          <NavItem to="/leads" icon={Users} label="Account" collapsed={!isExpanded} />
        )}
        {isModuleVisible('leads') && (
          <NavItem to="/final-qc" icon={CheckSquare} label="Final QC" collapsed={!isExpanded} />
        )}
        {isModuleVisible('leadAnalytics') && (
          <NavItem to="/lead-analytics" icon={BarChart3} label="Lead Analytics" collapsed={!isExpanded} />
        )}
        {isModuleVisible('tasks') && (
          <NavItem to="/tasks" icon={CheckSquare} label="Tasks" collapsed={!isExpanded} />
        )}
        {isModuleVisible('workflows') && (
          <NavItem to="/workflows" icon={GitFork} label="Workflows" collapsed={!isExpanded} />
        )}
        {isModuleVisible('reports') && (
          <NavItem to="/reports" icon={FileBarChart} label="Reports" collapsed={!isExpanded} />
        )}
        {/* iter326 — Admin Dashboard (admin-only, directly below Reports) */}
        {isModuleVisible('adminDashboard', true) && (
          <NavItem to="/admin-dashboard" icon={LayoutDashboard} label="Admin Dashboard" collapsed={!isExpanded} />
        )}

        {/* Calls Section */}
        <SectionHeader title="Calls" collapsed={!isExpanded} />
        {isModuleVisible('callHistory') && (
          <NavItem to="/call-history" icon={Phone} label="Call History" collapsed={!isExpanded} />
        )}
        {isModuleVisible('callAnalytics') && (
          <NavItem to="/call-analytics" icon={PieChart} label="Call Analytics" collapsed={!isExpanded} />
        )}
        {isModuleVisible('callToppers') && (
          <NavItem to="/call-toppers" icon={Award} label="Call Toppers" collapsed={!isExpanded} />
        )}

        {/* Properties Section */}
        <SectionHeader title="Properties" collapsed={!isExpanded} />
        {isModuleVisible('properties') && (
          <NavItem to="/properties" icon={Building2} label="Properties" collapsed={!isExpanded} />
        )}
        {isModuleVisible('hotelLifecycle') && (
          <NavItem to="/lifecycle" icon={GitMerge} label="Hotel Lifecycle" collapsed={!isExpanded} />
        )}

        {/* WhatsApp Section */}
        <SectionHeader title="WhatsApp" collapsed={!isExpanded} />
        {isModuleVisible('inbox') && (
          <NavItem to="/inbox" icon={Inbox} label="Inbox" collapsed={!isExpanded} />
        )}
        {isModuleVisible('wa_group_broadcast') && (
          <NavItem to="/wa-group-broadcast" icon={Radio} label="Group Broadcast" collapsed={!isExpanded} />
        )}
        {isModuleVisible('campaigns') && (
          <NavItem to="/campaigns" icon={Megaphone} label="Campaigns" collapsed={!isExpanded} />
        )}
        {isModuleVisible('broadcast') && (
          <NavItem to="/automation" icon={Send} label="Broadcast" collapsed={!isExpanded}>
            <NavItem to="/automation" icon={Plus} label="New Broadcast" collapsed={!isExpanded} isSubmenu />
            <NavItem to="/automation/scheduled" icon={Calendar} label="Scheduled" collapsed={!isExpanded} isSubmenu />
            <NavItem to="/automation/logs" icon={List} label="Logs" collapsed={!isExpanded} isSubmenu />
            <NavItem to="/automation/labels" icon={Tag} label="Labels" collapsed={!isExpanded} isSubmenu />
          </NavItem>
        )}
        {isModuleVisible('chatbot') && (
          <NavItem to="/chatbot" icon={Bot} label="Chatbot" collapsed={!isExpanded} />
        )}
        {isModuleVisible('whatsappAnalytics') && (
          <NavItem to="/whatsapp-analytics" icon={MessageSquare} label="Analytics" collapsed={!isExpanded} />
        )}

        {/* Account Section - Admin features */}
        <SectionHeader title="Account" collapsed={!isExpanded} />
        {isModuleVisible('auditHistory', true) && (
          <NavItem to="/audit-history" icon={History} label="Audit History" collapsed={!isExpanded} />
        )}
        {isModuleVisible('userManagement', true) && (
          <NavItem to="/settings/users" icon={Users2} label="User Management" collapsed={!isExpanded} />
        )}
        {isModuleVisible('settings', true) && (
          <NavItem to="/settings" icon={Settings} label="Settings" collapsed={!isExpanded} />
        )}
      </nav>

      {/* User Profile & Logout */}
      <div className="p-4 border-t border-gray-100">
        <div className="flex items-center gap-3 mb-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold ${
            isAdmin ? 'bg-gradient-to-br from-purple-500 to-purple-600' : 'bg-gradient-to-br from-teal-500 to-teal-600'
          }`}>
            {user?.name?.charAt(0)?.toUpperCase() || 'U'}
          </div>
          {isExpanded && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-800 truncate">{user?.name || 'User'}</p>
              <p className="text-xs text-gray-400 truncate">{user?.email}</p>
              {isAdmin && (
                <span className="inline-block px-1.5 py-0.5 bg-purple-100 text-purple-700 text-[10px] font-semibold rounded mt-0.5">
                  Admin
                </span>
              )}
            </div>
          )}
        </div>
        <button
          onClick={logout}
          className="w-full flex items-center gap-3 px-4 py-2.5 text-gray-600 hover:bg-red-50 hover:text-red-600 rounded-xl transition-colors"
        >
          <LogOut size={18} />
          {isExpanded && <span className="text-sm font-medium">Logout</span>}
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
